import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Search, CheckCircle2, Plus, Trash2, MessageCircle, Sparkles } from "lucide-react";
import Pager from "@/pages/admin/Pager";

const statusClass = (s) => s === "closed"
  ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
  : "bg-amber-500/20 text-amber-300 border-amber-500/30";

// Normalize a phone number for wa.me — strip everything non-digit; if 10 digits, prefix 91.
const waPhone = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

const buildCongratsMsg = (name, earned, total) => (
  `Hi ${name || "there"}, great news! Your referral has been successfully converted. ` +
  `You've earned ${earned} points for this referral. ` +
  `Your total Agility Points balance is now ${total} points. ` +
  `Keep referring and keep climbing the tiers! — Veloskin Agility Club`
);

const svcPointsFor = (svc, role) => {
  if (!svc) return 0;
  const legacy = Number(svc.points_value || 0);
  return Number((role === "affiliate" ? svc.affiliate_points : svc.customer_points) ?? legacy);
};

export default function ReferralsTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [services, setServices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [closing, setClosing] = useState(null);
  const [products, setProducts] = useState([{ service_id: "" }]);
  const [refereeName, setRefereeName] = useState("");
  const [refereePhone, setRefereePhone] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [closedResult, setClosedResult] = useState(null); // shows WhatsApp step after close

  const load = useCallback(() => {
    const params = { q, limit, skip };
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/admin/referrals", { params }).then((r) => {
      setRows(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [statusFilter, q, limit, skip]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [statusFilter, q]);
  useEffect(() => {
    api.get("/admin/services").then((r) => setServices(r.data.filter((s) => s.active)));
  }, []);

  const openClose = (r) => {
    setClosing(r);
    setProducts([{ service_id: "" }]);
    setRefereeName(""); setRefereePhone(""); setInvoiceNumber(""); setNote("");
    setClosedResult(null);
  };

  const referrerRole = closing?.referrer_role || "customer";
  const linePreview = useMemo(() => products.map((p) => {
    const svc = services.find((s) => s.id === p.service_id);
    return { svc, points: svcPointsFor(svc, referrerRole) };
  }), [products, services, referrerRole]);
  const totalPreview = linePreview.reduce((n, li) => n + (li.points || 0), 0);

  const updProduct = (i, sid) => setProducts(products.map((p, idx) => (idx === i ? { ...p, service_id: sid } : p)));
  const addProduct = () => setProducts([...products, { service_id: "" }]);
  const rmProduct = (i) => setProducts(products.length > 1 ? products.filter((_, idx) => idx !== i) : products);

  const submitClose = async () => {
    const ids = products.map((p) => p.service_id).filter(Boolean);
    if (ids.length === 0) return toast.error("Add at least one product");
    // dedupe (a shop could bill two of same product but here we treat multiples of same product as one line)
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/referrals/${closing.id}/close`, {
        service_ids: ids,
        referee_name: refereeName || null,
        referee_phone: refereePhone || null,
        invoice_number: invoiceNumber || null,
        note: note || null,
      });
      toast.success(`Closed · ${data.points_credited} pts credited to ${data.referrer_name}`);
      setClosedResult(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const waLink = useMemo(() => {
    if (!closedResult) return "";
    const msg = buildCongratsMsg(
      closedResult.referrer_name?.split(" ")[0],
      closedResult.points_credited,
      closedResult.referrer_new_balance,
    );
    return `https://wa.me/${waPhone(closedResult.referrer_phone)}?text=${encodeURIComponent(msg)}`;
  }, [closedResult]);

  const closeDialog = () => { setClosing(null); setClosedResult(null); };

  return (
    <div className="space-y-6">
      <div className="velo-card p-6">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" strokeWidth={1.5} />
            <Input data-testid="input-ref-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, referrer name, phone" className="velo-input pl-10 h-11" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="select-ref-status" className="velo-input h-11 w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#22103F] border-white/10 text-white">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="velo-card overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead className="bg-[#170929] border-b border-white/10">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-6 py-4">Code</th>
              <th className="px-6 py-4">Referrer</th>
              <th className="px-6 py-4">Shared on</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Products</th>
              <th className="px-6 py-4 text-right">Points</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <ReferralRow key={r.id} r={r} onClose={() => openClose(r)} />
            ))}
            {rows.length === 0 ? <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-500 text-sm">No referrals yet</td></tr> : null}
          </tbody>
        </table>
        <Pager
          total={total}
          limit={limit}
          skip={skip}
          testidPrefix="pager-referrals"
          onChange={({ limit: l, skip: s }) => { setLimit(l); setSkip(s); }}
        />
      </div>

      <Dialog open={!!closing} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="bg-[#22103F] border-white/10 text-white max-w-lg">
          {closing && !closedResult ? (
            <div>
              <DialogHeader><DialogTitle>Close referral {closing.code}</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-400 -mt-2 mb-4">Referrer: <span className="text-white">{closing.referrer_name}</span> <span className="capitalize ml-1">({closing.referrer_role})</span></p>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-slate-300 text-xs uppercase tracking-wider">Products in this transaction</Label>
                    <Button data-testid="btn-add-product" size="sm" variant="outline" onClick={addProduct} className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5 h-8">
                      <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2} /> Add product
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {products.map((p, i) => {
                      const li = linePreview[i];
                      return (
                        <div key={i} className="velo-surface rounded-lg p-3 flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <Select value={p.service_id} onValueChange={(v) => updProduct(i, v)}>
                              <SelectTrigger data-testid={`select-product-${i}`} className="velo-input h-10">
                                <SelectValue placeholder="Pick a product from the catalogue…" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#22103F] border-white/10 text-white">
                                {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-20 text-right">
                            <span data-testid={`points-preview-${i}`} className="font-mono text-sm text-pink-400">
                              {li?.svc ? `+${li.points}` : "—"}
                            </span>
                          </div>
                          <Button data-testid={`btn-remove-product-${i}`} size="sm" variant="ghost" onClick={() => rmProduct(i)} disabled={products.length <= 1} className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0">
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 flex items-center justify-between p-3 rounded-lg border border-pink-500/30 bg-pink-500/5">
                    <div className="text-xs text-slate-300">Total credit for <span className="capitalize">{referrerRole}</span> referrer</div>
                    <div data-testid="total-points-preview" className="font-mono text-pink-400 text-lg">+{totalPreview}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-xs uppercase tracking-wider">Referee name (optional)</Label>
                    <Input data-testid="input-referee-name" value={refereeName} onChange={(e) => setRefereeName(e.target.value)} className="velo-input mt-2 h-10" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs uppercase tracking-wider">Referee phone</Label>
                    <Input data-testid="input-referee-phone" value={refereePhone} onChange={(e) => setRefereePhone(e.target.value)} className="velo-input mt-2 h-10 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Invoice number (recommended)</Label>
                  <Input data-testid="input-close-invoice" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2026-142" className="velo-input mt-2 h-10 font-mono" />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">Note (optional)</Label>
                  <Input data-testid="input-close-note" value={note} onChange={(e) => setNote(e.target.value)} className="velo-input mt-2 h-10" />
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button data-testid="btn-submit-close" onClick={submitClose} disabled={busy || totalPreview === 0} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black">
                  <CheckCircle2 className="w-4 h-4 mr-2" strokeWidth={2} /> Close & credit {totalPreview > 0 ? `${totalPreview} pts` : "points"}
                </Button>
              </DialogFooter>
            </div>
          ) : null}

          {closedResult ? (
            <div>
              <DialogHeader><DialogTitle>Referral closed</DialogTitle></DialogHeader>
              <div className="text-xs text-slate-400 -mt-2 mb-4">Code {closing.code} · {closedResult.referrer_name}</div>
              <div className="space-y-3">
                <div className="velo-surface rounded-lg p-4 border border-emerald-500/20">
                  <div className="flex items-center gap-2 text-emerald-300 text-sm mb-2">
                    <Sparkles className="w-4 h-4" strokeWidth={2} /> +{closedResult.points_credited} points credited
                  </div>
                  <div className="text-xs text-slate-400">
                    New balance: <span className="text-white font-mono">{closedResult.referrer_new_balance}</span> pts
                  </div>
                  <div className="mt-3 space-y-1.5">
                    {(closedResult.line_items || []).map((li) => (
                      <div key={li.service_id} className="flex items-center justify-between text-xs">
                        <span className="text-slate-300">{li.service_name}</span>
                        <span className="text-pink-400 font-mono">+{li.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="velo-surface rounded-lg p-4 text-xs text-slate-300 leading-relaxed">
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-2">WhatsApp preview</div>
                  Hi {closedResult.referrer_name?.split(" ")[0]}, great news! Your referral has been successfully converted. You've earned <span className="text-pink-300 font-mono">{closedResult.points_credited}</span> points for this referral. Your total Agility Points balance is now <span className="text-pink-300 font-mono">{closedResult.referrer_new_balance}</span> points. Keep referring and keep climbing the tiers! — Veloskin Agility Club
                </div>
              </div>
              <DialogFooter className="mt-4 flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={closeDialog} className="rounded-full border-white/15 bg-transparent text-slate-300 hover:bg-white/5">Done</Button>
                <a
                  data-testid="btn-send-whatsapp"
                  href={waLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-emerald-500 hover:bg-emerald-400 text-black h-10 px-5 text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4 mr-2" strokeWidth={2} /> Send via WhatsApp
                </a>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReferralRow({ r, onClose }) {
  const isPending = r.status === "pending";
  const lines = r.line_items && r.line_items.length ? r.line_items : (r.service_name ? [{ service_id: r.service_id, service_name: r.service_name, points: r.points_credited }] : []);
  return (
    <tr data-testid={`referral-row-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02] align-top">
      <td className="px-6 py-4 text-sm font-mono text-white">{r.code}</td>
      <td className="px-6 py-4 text-sm text-white">
        <div>{r.referrer_name}</div>
        <div className="text-xs text-slate-500 font-mono">{r.referrer_phone} · <span className="capitalize">{r.referrer_role}</span></div>
      </td>
      <td className="px-6 py-4 text-xs text-slate-400">{format(new Date(r.created_at), "dd MMM yy, HH:mm")}</td>
      <td className="px-6 py-4"><Badge className={`border capitalize ${statusClass(r.status)}`}>{r.status}</Badge></td>
      <td className="px-6 py-4 text-xs text-slate-300">
        {lines.length === 0 ? <span className="text-slate-500">—</span> : (
          <div data-testid={`ref-products-${r.id}`} className="space-y-0.5">
            {lines.map((li) => (
              <div key={li.service_id} className="flex items-center gap-2">
                <span>{li.service_name}</span>
                {lines.length > 1 && <span className="text-[10px] text-pink-400 font-mono">+{li.points}</span>}
              </div>
            ))}
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-sm font-mono text-right text-pink-400">{r.points_credited > 0 ? `+${r.points_credited}` : "—"}</td>
      <td className="px-6 py-4 text-right">
        {isPending ? (
          <Button data-testid={`btn-close-referral-${r.id}`} size="sm" onClick={onClose} className="rounded-full bg-pink-500 hover:bg-pink-400 text-black h-8">
            Close
          </Button>
        ) : (
          <span className="text-xs text-slate-500">
            {r.closed_at ? format(new Date(r.closed_at), "dd MMM") : ""}
          </span>
        )}
      </td>
    </tr>
  );
}
